/**
 * @file recurrence-rule.schema.ts
 * @description Define um schema Zod reutilizável para regras de recorrência.
 * Este módulo pode ser importado por qualquer entidade que necessite
 * de uma funcionalidade de repetição (ex: JourneyStep, Activity).
 */

import { z } from 'zod';

// --- Helper Schemas & Enums ---

/**
 * Define a condição de término para uma regra de recorrência.
 */
const RecurrenceEndConditionSchema = z.union([
  // Termina após um número específico de ocorrências.
  z.object({ type: z.literal('COUNT'), value: z.number().int() }),
  // Termina em uma semana gestacional específica.
  z.object({ type: z.literal('UNTIL_GESTATIONAL_WEEK'), value: z.number().int() }),
  // Termina no final da gestação (baseado na DPP da gestação).
  z.object({ type: z.literal('END_OF_PREGNANCY') }),
]);


// --- Main RecurrenceRule Schema ---

/**
 * Define uma regra de recorrência completa para uma atividade ou etapa de jornada.
 */
const RecurrenceRuleSchema = z.object({
  /** Frequência da repetição (diária, semanal, mensal). */
  frequency: z.enum(['DAILY', 'WEEKLY', 'MONTHLY']),

  /** Intervalo da frequência. Ex: frequency: 'WEEKLY', interval: 2 -> a cada 2 semanas. */
  interval: z.number().int().positive(),

  /** A condição que determina quando a recorrência para. */
  endCondition: RecurrenceEndConditionSchema,
});


// --- Inferred TypeScript Types ---

type RecurrenceRule = z.infer<typeof RecurrenceRuleSchema>;
type RecurrenceEndCondition = z.infer<typeof RecurrenceEndConditionSchema>;


// --- Public Exports ---

export {
  RecurrenceRuleSchema,
  RecurrenceEndConditionSchema
};

export type {
  RecurrenceRule,
  RecurrenceEndCondition
};